// client/src/components/auth/profile/ProfileHeader.js
import React, { useState, useEffect } from 'react'
import FloatingInput from './FloatingInput'

function ProfileHeader({ userData, onUserDataChange, isEditing, onEditClick }) {
  const [formData, setFormData] = useState({
    firstname: userData?.firstname || '',
    lastname: userData?.lastname || '',
    email: userData?.email || '',
  })

  useEffect(() => {
    if (userData) {
      setFormData({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        email: userData.email || '',
      })
    }
  }, [userData, isEditing]) // formData resets if isEditing changes (e.g. on cancel from parent)

  const joinDate = new Date(userData.created_at).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    const newLocalFormData = { ...formData, [name]: value }
    setFormData(newLocalFormData)
    onUserDataChange(newLocalFormData) // Call onUserDataChange after local state update
  }

  return (
    <div className="profile-header">
      <style>{`
        .profile-header {
          background: var(--card-bg);
          border: var(--card-border);
          border-radius: var(--border-radius);
          padding: var(--card-padding);
          box-shadow: var(--shadow-sm);
          transition: box-shadow 0.2s ease, transform 0.12s ease;
          position: relative;
        }

        .profile-header:hover {
          box-shadow: var(--shadow-md);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
        }

        .accent-bar {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 6px;
          width: 100%;
          border-radius: 8px 8px 0 0;
          background: linear-gradient(90deg, #007bff 0%, #00c6ff 100%);
        }

        .avatar-section {
          flex-shrink: 0;
        }

        .avatar-placeholder {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #007bff, #0056b3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          box-shadow: 0 4px 14px rgba(0,0,0,0.15);
        }

        .user-info {
          flex-grow: 1;
          min-width: 0;
        }

        .user-name {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0 0 0.25rem 0;
          color: #212529;
        }

        .user-email {
          color: #6c757d;
          margin: 0 0 0.25rem 0;
          word-break: break-word;
          overflow-wrap: break-word;
        }

        .member-since {
          color: #6c757d;
          font-size: 0.875rem;
          margin: 0;
        }

        .edit-hint {
          color: #6c757d;
          font-size: 0.85rem;
          margin-top: 4px;
        }

        .edit-profile-button {
          margin-top: 0.75rem;
          padding: 0.5rem 1rem;
          background: var(--fl-button-neutral-bg);
          color: var(--fl-button-neutral-text);
          border: 1px solid var(--fl-button-neutral-border);
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px var(--fl-button-neutral-shadow);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .edit-profile-button:hover {
          background: var(--fl-button-neutral-bg-hover);
          border-color: var(--fl-button-neutral-border-hover);
          transform: translateY(-1px);
          box-shadow: 0 2px 6px var(--fl-button-neutral-shadow-hover);
        }

        .edit-profile-button:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px var(--fl-button-neutral-shadow);
          background: var(--fl-button-neutral-bg-active);
        }

        .edit-profile-button:focus-visible {
          outline: 3px solid var(--fl-focus-outline-color);
          outline-offset: 2px;
        }

        .edit-icon {
          font-size: 1rem;
        }

        /* Mobile layout adjustments */
        @media (max-width: 639px) {
          .header-content {
            flex-direction: column;
            align-items: stretch;
            gap: var(--spacing-sm);
          }

          .mobile-top-row {
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
          }

          .user-info {
            text-align: left;
          }
        }

        /* Desktop layout - restore normal flex behavior */
        @media (min-width: 640px) {
          .mobile-top-row {
            display: flex;
            align-items: center;
            gap: var(--spacing-md);
            flex-grow: 1;
          }

          .header-content {
            flex-direction: row;
            align-items: center;
          }
        }

        @media (min-width: 640px) {
          .avatar-placeholder {
            width: 80px;
            height: 80px;
            font-size: 1.75rem;
          }
        }

        @media (min-width: 768px) {
          .header-content {
            gap: var(--spacing-lg);
          }

          .user-name {
            font-size: 1.75rem;
          }
        }
      `}</style>

      <div className="accent-bar" />
      <div className="header-content">
        <div className="mobile-top-row">
          <div className="avatar-section">
            <div className="avatar-placeholder">🎾</div>
          </div>

          <div className="user-info">
            {!isEditing ? (
              <>
                <h1 className="user-name">
                  {userData.firstname} {userData.lastname}
                </h1>
                <p className="user-email">{userData.email}</p>
                <p className="member-since">Member since {joinDate}</p>
                <button
                  className="edit-profile-button"
                  onClick={() => onEditClick('firstname')}
                  aria-label="Edit profile information"
                >
                  <span className="edit-icon" aria-hidden="true">
                    ✏️
                  </span>
                  Edit Profile
                </button>
              </>
            ) : (
              <div className="edit-form">
                <div className="form-row name-row">
                  <FloatingInput
                    label="First Name"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChange}
                    required
                    disabled={!isEditing}
                    onDisabledClick={onEditClick}
                  />
                  <FloatingInput
                    label="Last Name"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChange}
                    required
                    disabled={!isEditing}
                    onDisabledClick={onEditClick}
                  />
                </div>
                <div className="form-row">
                  <FloatingInput
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    onDisabledClick={onEditClick}
                  />
                </div>
                {isEditing && (!formData.firstname || !formData.lastname) && (
                  <div className="required-indicator">
                    * First and last name are required
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProfileHeader
